import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Trophy, Medal, Star, Award, Users, TrendingUp, Crown,
  Zap, Target, BookOpen, Shield, ChevronDown, Search, Filter,
  BarChart3, Flame, Loader2
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { cn } from "../../lib/utils";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

const LEVEL_NAMES = ["Rookie", "Apprentice", "Skilled", "Expert", "Master", "Champion", "Legend"];
const LEVEL_COLORS = ["#94a3b8", "#60a5fa", "#34d399", "#fbbf24", "#f97316", "#ef4444", "#a855f7"];

const getLevelInfo = (level) => ({
  name: LEVEL_NAMES[Math.min(level - 1, LEVEL_NAMES.length - 1)],
  color: LEVEL_COLORS[Math.min(level - 1, LEVEL_COLORS.length - 1)],
  nextLevelPoints: level * 200,
});

const CHART_TOOLTIP = { borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12 };

const GamificationPage = () => {
  const { user } = useAuth();
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("leaderboard");
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [badges, setBadges] = useState([]);
  const [myPoints, setMyPoints] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [lbRes, bRes, pRes] = await Promise.all([
          api.get("/gamification/leaderboard"),
          api.get("/gamification/badges-catalog"),
          api.get("/gamification/my-points"),
        ]);
        setData(lbRes.data);
        setBadges(bRes.data || []);
        setMyPoints(pRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  if (loading) return (
    <AdminLayout title="Leaderboard" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  const lb = data?.leaderboard || [];
  const deptRankings = data?.department_rankings || [];
  const allDepts = [...new Set(lb.map(l => l.department))];

  const filtered = lb.filter(l => {
    if (deptFilter !== "all" && l.department !== deptFilter) return false;
    if (search && !l.user_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const top3 = lb.slice(0, 3);
  const myRank = lb.findIndex(l => l.user_id === user?.id) + 1;
  const myLevel = myPoints ? getLevelInfo(myPoints.level || 1) : getLevelInfo(1);

  return (
    <AdminLayout title="Leaderboard" variant={variant}>
      <motion.div className="space-y-6" variants={container} initial="hidden" animate="show" data-testid="leaderboard-page">
        {/* Header */}
        <motion.div variants={item}>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-0.5">Engagement & Recognition</p>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Training Leaderboard</h1>
          <p className="text-sm text-slate-500 mt-1">Recognize top performers and track engagement across your organization</p>
        </motion.div>

        {/* My Status Card */}
        {myPoints && (
          <motion.div variants={item}>
            <Card className="border shadow-none bg-gradient-to-r from-slate-900 to-slate-800 text-white">
              <CardContent className="p-5 flex items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
                  <Trophy className="w-8 h-8" style={{ color: myLevel.color }} />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-white/50 uppercase tracking-wider font-semibold">Your Standing</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-3xl font-black">{myPoints.points || 0}</span>
                    <span className="text-sm text-white/50">points</span>
                    <Badge className="text-xs border-0" style={{ backgroundColor: myLevel.color + "30", color: myLevel.color }}>{myLevel.name}</Badge>
                    {myRank > 0 && <Badge className="bg-white/10 text-white/80 border-0 text-xs">Rank #{myRank}</Badge>}
                  </div>
                  <div className="mt-2.5 max-w-xs">
                    <div className="flex items-center justify-between text-[10px] text-white/40 mb-1">
                      <span>Level {myPoints.level || 1}</span>
                      <span>{myLevel.nextLevelPoints - (myPoints.points || 0)} pts to next level</span>
                    </div>
                    <Progress value={((myPoints.points || 0) % 200) / 2} className="h-1.5 bg-white/10 [&>div]:bg-gradient-to-r" style={{ "--tw-gradient-from": myLevel.color }} />
                  </div>
                </div>
                <div className="hidden sm:grid grid-cols-3 gap-6 text-center shrink-0">
                  <div><p className="text-2xl font-bold">{myPoints.level || 1}</p><p className="text-[10px] text-white/40">Level</p></div>
                  <div><p className="text-2xl font-bold">{myPoints.streak_days || 0}</p><p className="text-[10px] text-white/40">Day Streak</p></div>
                  <div><p className="text-2xl font-bold">{myRank || "--"}</p><p className="text-[10px] text-white/40">Rank</p></div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Stats Row */}
        <motion.div variants={item} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Participants", value: data?.total_participants || 0, icon: Users, color: "#3b82f6", bg: "#3b82f612" },
            { label: "Total Points", value: (data?.total_points_earned || 0).toLocaleString(), icon: Zap, color: "#f59e0b", bg: "#f59e0b12" },
            { label: "Avg Points", value: data?.avg_points || 0, icon: BarChart3, color: "#10b981", bg: "#10b98112" },
            { label: "Departments", value: deptRankings.length, icon: Target, color: "#8b5cf6", bg: "#8b5cf612" },
          ].map((s, i) => (
            <Card key={i} className="border shadow-none"><CardContent className="p-3.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: s.bg }}><s.icon className="w-4 h-4" style={{ color: s.color }} /></div>
              <div><p className="text-lg font-bold leading-none">{s.value}</p><p className="text-[10px] text-muted-foreground">{s.label}</p></div>
            </CardContent></Card>
          ))}
        </motion.div>

        {/* Top 3 Podium */}
        {top3.length >= 3 && (
          <motion.div variants={item} className="grid grid-cols-3 gap-4">
            {[1, 0, 2].map(idx => {
              const l = top3[idx];
              if (!l) return null;
              const isFirst = idx === 0;
              const medals = ["text-amber-400", "text-slate-400", "text-amber-700"];
              const levelInfo = getLevelInfo(l.level);
              return (
                <Card key={l.user_id} className={cn("border shadow-none text-center", isFirst && "border-amber-300 bg-amber-50/30 -mt-2")}>
                  <CardContent className="p-5">
                    <div className="relative mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mb-2">
                      <span className="text-lg font-black text-slate-600">{(l.user_name || "?")[0]}</span>
                      <div className={cn("absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center shadow-sm", isFirst ? "bg-amber-400" : idx === 2 ? "bg-amber-700" : "bg-slate-400")}>
                        {isFirst ? <Crown className="w-3.5 h-3.5 text-white" /> : <Medal className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    <p className="text-sm font-bold">{l.user_name}</p>
                    <p className="text-[10px] text-muted-foreground">{l.department}</p>
                    <div className="mt-2 flex items-center justify-center gap-1">
                      <Zap className="w-3.5 h-3.5" style={{ color: levelInfo.color }} />
                      <span className="text-lg font-extrabold">{l.points.toLocaleString()}</span>
                    </div>
                    <Badge className="mt-1.5 text-[10px] border-0" style={{ backgroundColor: levelInfo.color + "20", color: levelInfo.color }}>{levelInfo.name}</Badge>
                    <div className="flex items-center justify-center gap-3 mt-2 text-[10px] text-muted-foreground">
                      <span><BookOpen className="w-3 h-3 inline mr-0.5" />{l.courses_completed}</span>
                      <span><Award className="w-3 h-3 inline mr-0.5" />{l.badge_count}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </motion.div>
        )}

        {/* Tabs */}
        <motion.div variants={item}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-muted/50 h-9">
              <TabsTrigger value="leaderboard" className="text-xs gap-1.5"><Trophy className="w-3.5 h-3.5" /> Full Leaderboard</TabsTrigger>
              <TabsTrigger value="departments" className="text-xs gap-1.5"><Target className="w-3.5 h-3.5" /> Department Rankings</TabsTrigger>
              <TabsTrigger value="badges" className="text-xs gap-1.5"><Award className="w-3.5 h-3.5" /> Badges</TabsTrigger>
            </TabsList>
          </Tabs>
        </motion.div>

        {/* Leaderboard Table */}
        {activeTab === "leaderboard" && (
          <motion.div variants={item} className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-sm" />
              </div>
              <Select value={deptFilter} onValueChange={setDeptFilter}>
                <SelectTrigger className="w-[180px] h-9 text-xs"><Filter className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Departments</SelectItem>{allDepts.map(d => <SelectItem key={d} value={d} className="text-xs">{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Card className="border shadow-none" data-testid="leaderboard-table">
              <CardContent className="p-0">
                <div className="grid grid-cols-12 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-5 py-2.5 border-b bg-muted/30">
                  <span className="col-span-1">Rank</span><span className="col-span-3">Name</span><span className="col-span-2">Department</span>
                  <span className="col-span-1 text-center">Level</span><span className="col-span-1 text-right">Points</span>
                  <span className="col-span-1 text-center">Courses</span><span className="col-span-1 text-center">Compliance</span>
                  <span className="col-span-1 text-center">Badges</span><span className="col-span-1 text-center">Streak</span>
                </div>
                {filtered.length === 0 ? <div className="text-center py-8 text-sm text-muted-foreground">No users found</div> :
                filtered.map(l => {
                  const levelInfo = getLevelInfo(l.level);
                  const isMe = l.user_id === user?.id;
                  return (
                    <div key={l.user_id} className={cn("grid grid-cols-12 items-center px-5 py-3 border-b hover:bg-muted/20 transition-colors", isMe && "bg-primary/5")} data-testid={`lb-row-${l.rank}`}>
                      <span className="col-span-1">
                        {l.rank <= 3 ? (
                          <div className={cn("w-7 h-7 rounded-full flex items-center justify-center", l.rank === 1 ? "bg-amber-100" : l.rank === 2 ? "bg-slate-100" : "bg-amber-50")}>
                            {l.rank === 1 ? <Crown className="w-3.5 h-3.5 text-amber-500" /> : <span className="text-xs font-bold text-muted-foreground">{l.rank}</span>}
                          </div>
                        ) : <span className="text-sm font-bold text-muted-foreground ml-1.5">{l.rank}</span>}
                      </span>
                      <span className="col-span-3 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><span className="text-xs font-bold text-slate-600">{(l.user_name || "?")[0]}</span></div>
                        <div><p className="text-sm font-medium">{l.user_name} {isMe && <Badge className="ml-1 text-[8px] bg-primary/10 text-primary border-0">You</Badge>}</p></div>
                      </span>
                      <span className="col-span-2 text-xs text-muted-foreground">{l.department}</span>
                      <span className="col-span-1 text-center"><Badge className="text-[9px] border-0 px-2" style={{ backgroundColor: levelInfo.color + "20", color: levelInfo.color }}>{l.level}</Badge></span>
                      <span className="col-span-1 text-right text-sm font-bold">{l.points.toLocaleString()}</span>
                      <span className="col-span-1 text-center text-xs">{l.courses_completed}</span>
                      <span className="col-span-1 text-center text-xs">{l.compliance_completed}</span>
                      <span className="col-span-1 text-center text-xs flex items-center justify-center gap-0.5"><Award className="w-3 h-3 text-amber-500" />{l.badge_count}</span>
                      <span className="col-span-1 text-center text-xs flex items-center justify-center gap-0.5">{l.streak_days > 0 && <><Flame className="w-3 h-3 text-orange-500" />{l.streak_days}d</>}</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Department Rankings */}
        {activeTab === "departments" && (
          <motion.div variants={item} className="space-y-4">
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Department Points Comparison</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={deptRankings.slice(0, 10)} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="department" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={CHART_TOOLTIP} />
                    <Bar dataKey="total_points" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Total Points" />
                    <Bar dataKey="avg_points" fill="#10b981" radius={[4, 4, 0, 0]} name="Avg Points" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <div className="space-y-2">
              {deptRankings.map((d, i) => (
                <Card key={d.department} className="border shadow-none">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold", i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-slate-100 text-slate-700" : "bg-blue-50 text-blue-700")}>{i + 1}</div>
                      <div><p className="text-sm font-bold">{d.department}</p><p className="text-[10px] text-muted-foreground">{d.members} members</p></div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right"><p className="text-lg font-extrabold">{d.total_points.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">total points</p></div>
                      <div className="text-right"><p className="text-sm font-bold">{d.avg_points}</p><p className="text-[10px] text-muted-foreground">avg/member</p></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {deptRankings.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No department data available</p>}
            </div>
          </motion.div>
        )}

        {/* Badges Catalog */}
        {activeTab === "badges" && (
          <motion.div variants={item} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {badges.map((b, i) => (
              <Card key={i} className="border shadow-none hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                    <Award className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-sm font-bold">{b.name}</p>
                    <p className="text-xs text-muted-foreground">{b.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
            {badges.length === 0 && <p className="col-span-3 text-center py-8 text-sm text-muted-foreground">No badges available yet</p>}
          </motion.div>
        )}
      </motion.div>
    </AdminLayout>
  );
};

export default GamificationPage;
